import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import api from '../services/api';

const ExecutionContext = createContext();

export const ExecutionProvider = ({ children }) => {
  // Global execution state
  const [runningExecution, setRunningExecution] = React.useState(null);
  const [pollingError, setPollingError] = React.useState(null);
  const [pollingInterval, setPollingInterval] = React.useState(15000); // Default 15 seconds
  const intervalRef = useRef(null);
  
  // Legacy refresh trigger for backward compatibility
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  
  // Execution completion tracking
  const [completedExecutions, setCompletedExecutions] = React.useState(new Set());
  const [executionCompletionTrigger, setExecutionCompletionTrigger] = React.useState(0);
  const previousRunningExecution = useRef(null);
  
  // Execution start tracking
  const [executionStartTrigger, setExecutionStartTrigger] = React.useState(0);

  // Centralized execution status fetching
  const fetchRunningExecution = useCallback(async () => {
    try {
      const response = await api.getRunningExecution();
      const execution = response.data;
      
      // Check if an execution just completed
      if (previousRunningExecution.current && !execution) {
        const completedExecution = previousRunningExecution.current;
        const completedExecutionId = completedExecution.execution_id;
        
        // Add to completed executions set and trigger completion notification
        setCompletedExecutions(prev => new Set([...prev, completedExecutionId]));
        setExecutionCompletionTrigger(prev => prev + 1);
        
        console.log(`Execution completed: ${completedExecution.executed_module} (ID: ${completedExecutionId})`);
      }
      
      // Update previous execution reference
      previousRunningExecution.current = execution;
      
      if (execution) {
        setRunningExecution(execution);
        // Switch to fast polling (2 seconds) when execution is running
        if (pollingInterval !== 2000) {
          setPollingInterval(2000);
        }
      } else {
        setRunningExecution(null);
        // Switch back to slow polling (15 seconds) when no execution is running
        if (pollingInterval !== 15000) {
          setPollingInterval(15000);
        }
      }
      
      // Clear any previous errors
      setPollingError(null);
    } catch (error) {
      console.error('Error fetching running execution:', error);
      setPollingError('Failed to fetch execution status');
      // Don't clear running execution on error to avoid flickering
    }
  }, [pollingInterval]);

  // Set up adaptive polling for execution status
  useEffect(() => {
    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Initial fetch
    fetchRunningExecution();

    // Set up polling with current interval
    intervalRef.current = setInterval(fetchRunningExecution, pollingInterval);

    // Cleanup interval on component unmount or interval change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [pollingInterval, fetchRunningExecution]);

  // React to refresh triggers
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchRunningExecution();
    }
  }, [refreshTrigger, fetchRunningExecution]);

  // Helper functions for modules
  const isModuleRunning = useCallback((moduleName) => {
    return runningExecution?.executed_module === moduleName;
  }, [runningExecution]);

  const getModuleProgress = useCallback((moduleName) => {
    if (runningExecution?.executed_module === moduleName) {
      return runningExecution.current_progress || 0;
    }
    return 0;
  }, [runningExecution]);

  const getRunningExecutionId = useCallback((moduleName) => {
    if (runningExecution?.executed_module === moduleName) {
      return runningExecution.execution_id;
    }
    return null;
  }, [runningExecution]);

  const isAnyModuleRunning = useCallback(() => {
    return runningExecution !== null;
  }, [runningExecution]);

  const getRunningModuleName = useCallback(() => {
    return runningExecution?.executed_module || null;
  }, [runningExecution]);

  // Function to check if a specific execution has completed
  const hasExecutionCompleted = useCallback((executionId) => {
    return completedExecutions.has(executionId);
  }, [completedExecutions]);

  // Function to get completion trigger for modules to subscribe to
  const getCompletionTrigger = useCallback(() => {
    return executionCompletionTrigger;
  }, [executionCompletionTrigger]);

  // Function to trigger immediate polling refresh (legacy compatibility)
  const triggerRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Function to start execution tracking for a module
  const startExecution = useCallback(async (moduleName, executionId) => {
    // Immediately set the running state to provide instant feedback
    setRunningExecution({
      executed_module: moduleName,
      execution_id: executionId,
      current_progress: 0
    });
    
    // Switch to fast polling
    setPollingInterval(2000);
    
    // Trigger execution start notification
    setExecutionStartTrigger(prev => prev + 1);
    
    // Trigger immediate refresh
    triggerRefresh();
    
    console.log(`Execution started: ${moduleName} (ID: ${executionId})`);
  }, [triggerRefresh]);

  // Function to stop execution tracking
  const stopExecution = useCallback(() => {
    setRunningExecution(null);
    setPollingInterval(15000);
  }, []);

  // Function to cancel execution
  const cancelExecution = useCallback(async () => {
    try {
      await api.cancelExecution(false);
      // Trigger immediate refresh to check execution status
      triggerRefresh();
      return { success: true };
    } catch (error) {
      console.error('Error cancelling execution:', error);
      return { success: false, error: error.message };
    }
  }, [triggerRefresh]);

  const value = {
    // Global execution state
    runningExecution,
    pollingError,
    
    // Helper functions
    isModuleRunning,
    getModuleProgress,
    getRunningExecutionId,
    isAnyModuleRunning,
    getRunningModuleName,
    
    // Completion tracking
    hasExecutionCompleted,
    getCompletionTrigger,
    executionCompletionTrigger,
    
    // Start tracking
    executionStartTrigger,
    
    // Control functions
    startExecution,
    stopExecution,
    cancelExecution,
    fetchRunningExecution,
    
    // Legacy compatibility
    refreshTrigger,
    triggerRefresh
  };

  return (
    <ExecutionContext.Provider value={value}>
      {children}
    </ExecutionContext.Provider>
  );
};

export const useExecution = () => {
  const context = useContext(ExecutionContext);
  if (!context) {
    throw new Error('useExecution must be used within an ExecutionProvider');
  }
  return context;
};